import { RouteFrameCode } from 'components/common/types/Deal.types';
import { FileContent, FileList } from 'components/common/types';
import {
  DirectSalesFilter,
  DirectSalesFilterGroupNames,
  DirectSalesFilterTypes,
  DirectSalesNestedFilter,
  DirectSalesUnitListFilter,
} from 'components/common/types/DirectSalesCampaign.types';
import { File } from 'utils/framesUtils.types';

export const mergeFilesByFilename = (listFiles: FileList[], listFilesExcluded: FileList[]): FileList[] => {
  const allFiles = [...listFiles, ...listFilesExcluded];

  const mergedFiles = allFiles.reduce<FileList[]>((acc, { name, include, file, fileContents }: FileList) => {
    const existingFile = acc.find((existing: FileList) => existing.name === name);

    if (existingFile) {
      existingFile.fileContents = [...(existingFile.fileContents || []), ...(fileContents || [])];
      existingFile.include = existingFile.include ?? include;
    } else {
      acc.push({
        name,
        include,
        file,
        fileContents: fileContents ? [...fileContents] : [],
      });
    }

    return acc;
  }, []);

  return mergedFiles.map(({ fileContents, name, file }) => {
    const includes = (fileContents || []).map(({ include }) => include);
    const allSame = new Set(includes).size === 1;

    return {
      file,
      name,
      include: allSame ? includes[0] : undefined,
      fileContents,
    };
  });
};

export const mapIndividuallySelectedFrameData = ({
  frames,
  include,
}: {
  frames: string[] | null;
  include: boolean;
}): FileContent[] => {
  if (!frames) return [];

  return frames.map((id) => ({
    include,
    code: id,
    name: id,
  }));
};

export const getIndividualRouteFrameCodes = (
  includedFrames: DirectSalesUnitListFilter[],
  excludedFrames: DirectSalesUnitListFilter[],
): FileContent[] => {
  const excludedIndividualSelectedFileFilters = excludedFrames.find(({ filename }) => !filename);
  const includedIndividualSelectedFrames = includedFrames.find(({ filename }) => !filename);

  const includedRouteFrameCodes = includedIndividualSelectedFrames
    ? mapIndividuallySelectedFrameData({
        frames: includedIndividualSelectedFrames.unitsIds,
        include: true,
      })
    : [];
  const excludedRouteFrameCodes = excludedIndividualSelectedFileFilters
    ? mapIndividuallySelectedFrameData({
        frames: excludedIndividualSelectedFileFilters.unitsIds,
        include: false,
      })
    : [];

  return [...includedRouteFrameCodes, ...excludedRouteFrameCodes];
};

export const mapUploadedFileData = ({
  uploadedFiles,
  include,
}: {
  uploadedFiles: DirectSalesUnitListFilter[];
  include: boolean;
}): FileList[] => {
  return uploadedFiles.map(({ filename, unitsIds }) => {
    const file = {
      path: filename,
      name: filename,
    } as Partial<File>;

    return {
      include,
      file,
      name: filename,
      fileContents: mapIndividuallySelectedFrameData({ frames: unitsIds, include }),
    } as FileList;
  });
};

export const getUploadedRouteFrameCodes = (
  includedFrames: DirectSalesUnitListFilter[],
  excludedFrames: DirectSalesUnitListFilter[],
): FileList[] => {
  const excludedUploadedFileFilters = excludedFrames.filter(({ filename }) => filename !== null);
  const includedUploadedFileFilters = includedFrames.filter(({ filename }) => filename !== null);

  const includedListFiles = mapUploadedFileData({ uploadedFiles: includedUploadedFileFilters, include: true });
  const excludedListFiles = mapUploadedFileData({ uploadedFiles: excludedUploadedFileFilters, include: false });

  return mergeFilesByFilename(includedListFiles, excludedListFiles);
};

export const transformUnitListFiltersFromResponse = (
  filter?: DirectSalesFilter,
): {
  listFiles: FileList[];
  routeFrameCodes: RouteFrameCode[];
  mapRouteFrameCodes: RouteFrameCode[];
} => {
  if (!filter) {
    return {
      listFiles: [],
      routeFrameCodes: [],
      mapRouteFrameCodes: [],
    };
  }

  const notFilters = filter?.filters.find(
    ({ type }) => type === DirectSalesFilterTypes.NotFilter,
  ) as DirectSalesNestedFilter;

  const excludedFilters = notFilters
    ? (notFilters.filter.filters.filter(
        ({ type }) => type === DirectSalesFilterGroupNames.UnitListFilter,
      ) as DirectSalesUnitListFilter[])
    : [];

  const includedRouteFrameFilters = filter.filters
    .filter(
      (includedFilter: DirectSalesFilter) =>
        includedFilter.type === DirectSalesFilterTypes.OrFilter &&
        includedFilter.filters.some((subFilter) => subFilter.type === DirectSalesFilterGroupNames.UnitListFilter),
    )
    .flatMap((flatIncludedFilter: DirectSalesFilter) =>
      flatIncludedFilter.filters.filter((subFilter) => subFilter.type === DirectSalesFilterGroupNames.UnitListFilter),
    ) as DirectSalesUnitListFilter[];

  const includedMapRouteFrameFilters = filter.filters
    .filter(
      (includedFilter: DirectSalesFilter) =>
        includedFilter.type === DirectSalesFilterTypes.OrFilter &&
        includedFilter.filters.some(
          (subFilter) => subFilter.type === DirectSalesFilterGroupNames.MapSelectionListFilter,
        ),
    )
    .flatMap((flatIncludedFilter: DirectSalesFilter) =>
      flatIncludedFilter.filters.filter(
        (subFilter) => subFilter.type === DirectSalesFilterGroupNames.MapSelectionListFilter,
      ),
    ) as DirectSalesUnitListFilter[];

  const listFiles = getUploadedRouteFrameCodes(includedRouteFrameFilters, excludedFilters);
  const routeFrameCodes = getIndividualRouteFrameCodes(includedRouteFrameFilters, excludedFilters);
  const mapRouteFrameCodes = getIndividualRouteFrameCodes(includedMapRouteFrameFilters, []);

  return {
    listFiles: listFiles ?? [],
    routeFrameCodes: routeFrameCodes ?? [],
    mapRouteFrameCodes: mapRouteFrameCodes ?? [],
  };
};
